import { db, withSyncWrite } from '../../db/indexeddb'
import {
  getCloudTasks,
  getCloudTaskIds,
  getCloudTasksByIds,
  getDeletedCloudTasks,
  createCloudTask,
  updateCloudTask,
  deleteCloudTask,
} from '../../supabase/queries'
import { CloudTaskSchema, CloudTaskPayloadSchema } from '../sync-schemas'
import {
  resolveCloudProjectId,
  resolveCloudSectionId,
  resolveLocalProjectId,
  resolveLocalSectionId,
  resolveCloudTaskId,
  resolveLocalTaskId,
  buildLocalToCloudTagIdMap,
  buildCloudToLocalTagIdMap,
} from '../sync-resolve'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { RecurrenceRule, Task } from '../../db/models'
import { dateToISO, startDateToISO } from './dates'

/* ── Task ─────────────────────────────────────────── */

/**
 * Pick a lifespace for a task pulled from cloud. Pre-PR-1 payloads may arrive
 * without `life_space` (stale client, or the column was added between pull
 * windows). Fallback mirrors the Dexie v16 upgrade: lookup via project,
 * otherwise `'personal'`.
 *
 * Stand hier statt im gemeinsamen Kopf: Nur die Aufgabe braucht ihn.
 */
async function resolveTaskLifeSpace(
  remoteLifeSpace: 'personal' | 'work' | 'home' | null | undefined,
  localProjectId: string | null | undefined,
): Promise<'personal' | 'work' | 'home'> {
  if (remoteLifeSpace) return remoteLifeSpace
  if (localProjectId) {
    const project = await db.projects.get(localProjectId)
    if (project?.lifeSpace) return project.lifeSpace
  }
  return 'personal'
}

/**
 * Das lokale Elternteil einer Unteraufgabe (T14 #179). `undefined`: keins genannt;
 * `null`: genannt, aber (noch) nicht auf diesem Gerät.
 */
async function resolveLocalParent(remote: { parent_task_id?: string | null; parent_local_id?: string | null }): Promise<string | null | undefined> {
  if (!remote.parent_task_id && !remote.parent_local_id) return undefined
  return resolveLocalTaskId(remote.parent_task_id, remote.parent_local_id)
}

type ParentRef = { id: string; local_id?: string | null; parent_task_id?: string | null; parent_local_id?: string | null }

/**
 * **Eltern vor Kind, beliebig tief** (#181): sortiert nach der Tiefe im Stapel, über Cloud- und
 * lokale Kennung. Vorher kam nur „hat Eltern-Teil“ nach hinten — ein Enkel vor seinem Eltern-Teil
 * fand es nicht und wurde übersprungen. Ein Eltern-Teil außerhalb des Stapels liegt schon auf dem
 * Gerät oder fehlt; ein Zyklus hält an.
 */
function parentsFirst<T extends ParentRef>(records: readonly T[]): T[] {
  const byId = new Map(records.map(r => [r.id, r]))
  const byLocalId = new Map(records.flatMap(r => (r.local_id ? [[r.local_id, r] as const] : [])))
  const parentOf = (r: T) => (r.parent_task_id ? byId.get(r.parent_task_id) : undefined)
    ?? (r.parent_local_id ? byLocalId.get(r.parent_local_id) : undefined)
  const depth = new Map<T, number>()
  for (const record of records) {
    let d = 0
    const seen = new Set([record])
    for (let p = parentOf(record); p && !seen.has(p); p = parentOf(p)) { d++; seen.add(p) }
    depth.set(record, d)
  }
  return [...records].sort((a, b) => depth.get(a)! - depth.get(b)!)
}

/**
 * Schlösse `parentId` als Eltern-Teil von `id` einen Zyklus (#181)? Zwei Geräte rücken über Kreuz
 * ein — hier B unter A, dort A unter B. Beide stünden dann ohne Aufgabe über sich und verschwänden
 * aus jeder Liste; der eigene Stand bleibt besser stehen.
 */
async function closesLoop(id: string | undefined, parentId: string | null | undefined): Promise<boolean> {
  if (!id || !parentId) return false
  return parentId === id || (await db.getAncestorTasks(parentId)).some(a => a.id === id)
}

/** Kinder warten auf die Cloud-Kennung ihres Elternteils — wie früher `subtasks.task_id`. */
async function parentPending(task: Task): Promise<boolean> {
  return task.parentTaskId != null && !(await resolveCloudTaskId(task.parentTaskId))
}

/**
 * Eine hier umgewandelte Unteraufgabe trifft ihre Zeile in der Cloud (ein anderes Gerät war
 * schneller). Die Kennung wird übernommen, und die neuere Seite gewinnt: Ist die Cloud
 * jünger, kommen ihre Werte her; sonst geht der Stand dieses Geräts als Update hoch —
 * sein `create` fiele sonst als „schon verknüpft" weg.
 */
async function adoptRemoteRow(local: Task, remote: any): Promise<void> {
  if (new Date(remote.updated_at) > local.updatedAt) {
    const changes = await taskPullDescriptor.buildLocalUpdate(remote, local)
    await withSyncWrite(() => db.tasks.update(local.id, { ...changes, cloudId: remote.id, userId: remote.user_id, synced: true, syncedAt: new Date() }))
    return
  }
  await db.tasks.update(local.id, { cloudId: remote.id, userId: remote.user_id })
  await db.queueSyncChange('update', 'tasks', { localId: local.id })
}

export const taskPullDescriptor: PullDescriptor<any> = {
  entityName: 'task',
  settingsKey: 'lastSyncTime',

  fetchRemote: (userId, since) => getCloudTasks(userId, since),
  fetchRemoteIds: (userId) => getCloudTaskIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudTasksByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudTasks(userId, since),

  safeParse: (raw) => CloudTaskSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.tasks,
  getAllLocal: () => db.tasks.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.tasks.filter(t => !t.deletedAt).count()
    return count === 0
  },

  // Elternteile zuerst: Ein Kind im selben Durchgang findet sein Elternteil dann schon vor.
  orderRemote: (records) => parentsFirst(records),

  /*
    **Kommentare und Erinnerungen gehen NICHT mit** (Diff-Audit 30.09.2026). Nimmt ein Gerät den
    Haken einer Wiederkehr zurück, wandern sie von der gelöschten Runde an die alte zurück — und
    dieses Löschen kommt hier vor dem Umhängen an. Eine Kaskade löschte sie und schickte das
    Löschen an alle Geräte. Ihr eigenes Löschen bringt der Pull; bis dahin hängen sie an einer
    gelöschten Aufgabe, die nirgends steht, und Erinnerungen schweigen dort (`reminderSilence`).
    Der Preis: Ein Kommentar, den ein Gerät schrieb, während das andere die Aufgabe löschte,
    bleibt in der Cloud an der toten Aufgabe — unsichtbar, und mit ihr zurück, wenn sie
    zurückkommt.
  */

  buildLocalInsert: async (remote) => {
    // Hat dieses Gerät die Unteraufgabe hinter der Zeile schon selbst umgewandelt, ist
    // es dieselbe Zeile: Kennung übernehmen statt eine zweite anlegen (T14 #179).
    const adopting = await db.tasks.where('adoptCloudId').equals(remote.id).first()
    if (adopting) {
      if (!adopting.cloudId) await adoptRemoteRow(adopting, remote)
      return null
    }
    const parentTaskId = await resolveLocalParent(remote)
    // Elternteil nicht auf dem Gerät (hier gelöscht, oder seine Zeile fiel durch die
    // Prüfung): überspringen. Ohne Elternteil angelegt stünde die Unteraufgabe als
    // Aufgabe in jeder Liste. Geholt wird sie erst wieder, wenn sie sich in der Cloud
    // ändert — nach einem Löschen des Elternteils ist Weglassen richtig.
    if (parentTaskId === null) return null
    const localProjectId = await resolveLocalProjectId(remote.project_id)
    const localSectionId = await resolveLocalSectionId(remote.section_id)
    const tagIdMap = await buildCloudToLocalTagIdMap()
    const localTags = (remote.tags ?? []).flatMap((cloudId: string) => {
      const local = tagIdMap[cloudId]
      return local ? [local] : []
    })
    return {
      id: crypto.randomUUID(),
      title: remote.title,
      description: remote.description ?? null,
      status: remote.completed ? 'completed' : 'todo',
      attention: remote.attention ?? null,
      category: 'other',
      lifeSpace: await resolveTaskLifeSpace(remote.life_space, localProjectId),
      projectId: localProjectId,
      sectionId: localSectionId,
      userId: remote.user_id,
      cloudId: remote.id,
      dueDate: remote.due_date ? new Date(remote.due_date) : null,
      dueTime: remote.due_time ?? null,
      duePeriod: remote.due_period ?? null,
      startDate: remote.start_date ? new Date(remote.start_date) : null,
      startTime: remote.start_time ?? null,
      startPeriod: remote.start_period ?? null,
      completedAt: remote.completed ? new Date(remote.updated_at) : null,
      estimatedDuration: remote.estimated_duration ?? null,
      actualDuration: null,
      durationMode: remote.duration_mode ?? null,
      tags: localTags,
      parentTaskId: parentTaskId ?? null,
      order: remote.order,
      dayOrder: remote.day_order ?? null,
      deletedAt: null,
      pausedAt: remote.paused_at ? new Date(remote.paused_at) : null,
      rewardId: remote.reward_id ?? null,
      recurrence: (remote.recurrence ?? null) as RecurrenceRule | null,
      importedFrom: remote.imported_from ?? null,
      favoriteOrder: remote.favorite_order ?? null,
      completedCount: remote.completed_count ?? null,
      postponedCount: remote.postponed_count ?? null,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
    }
  },

  buildLocalUpdate: async (remote, existing) => {
    const parentTaskId = await resolveLocalParent(remote)
    const loops = await closesLoop(existing?.id, parentTaskId)
    const localProjectId = await resolveLocalProjectId(remote.project_id)
    const localSectionId = await resolveLocalSectionId(remote.section_id)
    const tagIdMap = await buildCloudToLocalTagIdMap()
    const localTags = (remote.tags ?? []).flatMap((cloudId: string) => {
      const local = tagIdMap[cloudId]
      return local ? [local] : []
    })
    return {
      title: remote.title,
      description: remote.description ?? null,
      status: remote.completed ? 'completed' : 'todo',
      completedAt: remote.completed ? new Date(remote.updated_at) : null,
      ...('life_space' in remote && remote.life_space
        ? { lifeSpace: remote.life_space }
        : {}),
      projectId: localProjectId,
      sectionId: localSectionId,
      tags: localTags,
      attention: remote.attention ?? null,
      estimatedDuration: remote.estimated_duration ?? null,
      durationMode: remote.duration_mode ?? null,
      ...('start_date' in remote ? { startDate: remote.start_date ? new Date(remote.start_date) : null } : {}),
      ...('start_time' in remote ? { startTime: remote.start_time ?? null } : {}),
      ...('start_period' in remote ? { startPeriod: remote.start_period ?? null } : {}),
      ...('due_date' in remote ? { dueDate: remote.due_date ? new Date(remote.due_date) : null } : {}),
      ...('due_time' in remote ? { dueTime: remote.due_time ?? null } : {}),
      ...('due_period' in remote ? { duePeriod: remote.due_period ?? null } : {}),
      ...('paused_at' in remote ? { pausedAt: remote.paused_at ? new Date(remote.paused_at) : null } : {}),
      ...('reward_id' in remote ? { rewardId: remote.reward_id ?? null } : {}),
      ...('recurrence' in remote ? { recurrence: (remote.recurrence ?? null) as RecurrenceRule | null } : {}),
      order: remote.order,
      // Nur übernehmen, wenn die Spalte im Datensatz steht: Ein Gerät mit
      // älterem Stand sendet sie nicht, und ein fehlendes Feld darf die
      // gezogene Reihenfolge hier nicht auf null zurücksetzen.
      ...('day_order' in remote ? { dayOrder: remote.day_order ?? null } : {}),
      ...('imported_from' in remote ? { importedFrom: remote.imported_from ?? null } : {}),
      // Fehlt die Spalte (Cloud ohne Migration), bleibt der Stern hier stehen (#210).
      ...('favorite_order' in remote ? { favoriteOrder: remote.favorite_order ?? null } : {}),
      // Ein Zähler wird nie geleert: Ohne Spalte oder mit null bleibt der lokale Stand (#198).
      ...(remote.completed_count != null ? { completedCount: remote.completed_count } : {}),
      ...(remote.postponed_count != null ? { postponedCount: remote.postponed_count } : {}),
      // Ein unauflösbares Elternteil lässt den lokalen Wert stehen, statt die Zeile zur Aufgabe zu machen;
      // ebenso eins, das einen Zyklus schlösse.
      ...(parentTaskId !== null && !loops && ('parent_task_id' in remote || 'parent_local_id' in remote) ? { parentTaskId: parentTaskId ?? null } : {}),
      updatedAt: new Date(remote.updated_at),
    }
  },
}

export const taskPushDescriptor: PushDescriptor<Task> = {
  entityName: 'CloudTask',
  getLocal: (id) => db.getTask(id),
  getLocalCloudId: (t) => t.cloudId,
  getLocalId: (t) => t.id,
  table: db.tasks,

  canCreate: async (task) => {
    if (await parentPending(task)) return { defer: true }
    if (task.projectId) {
      const cloudProjectId = await resolveCloudProjectId(task.projectId)
      if (!cloudProjectId) return { defer: true }
    }
    return { defer: false }
  },

  canUpdate: async (task) => {
    if (await parentPending(task)) return { defer: true }
    if (task.projectId) {
      const cloudProjectId = await resolveCloudProjectId(task.projectId)
      if (!cloudProjectId) return { defer: true }
    }
    return { defer: false }
  },

  buildCreatePayload: async (task) => {
    const cloudProjectId = await resolveCloudProjectId(task.projectId)
    const cloudSectionId = await resolveCloudSectionId(task.sectionId)
    const tagIdMap = await buildLocalToCloudTagIdMap()
    const cloudTags = task.tags.flatMap(localId => {
      const cloud = tagIdMap[localId]
      return cloud ? [cloud] : []
    })
    const payload = CloudTaskPayloadSchema.parse({
      title: task.title,
      description: task.description,
      completed: task.status === 'completed',
      life_space: task.lifeSpace,
      project_id: cloudProjectId,
      section_id: cloudSectionId,
      order: task.order,
      day_order: task.dayOrder ?? null,
      tags: cloudTags,
      attention: task.attention,
      estimated_duration: task.estimatedDuration,
      duration_mode: task.durationMode,
      start_date: startDateToISO(task.startDate, task.startTime),
      start_time: task.startTime ?? null,
      start_period: task.startPeriod ?? null,
      due_date: dateToISO(task.dueDate, task.dueTime),
      due_time: task.dueTime ?? null,
      due_period: task.duePeriod ?? null,
      paused_at: task.pausedAt ? task.pausedAt.toISOString() : null,
      reward_id: task.rewardId ?? null,
      recurrence: task.recurrence ?? null,
      local_id: task.id,
      imported_from: task.importedFrom ?? null,
      parent_task_id: await resolveCloudTaskId(task.parentTaskId),
      parent_local_id: task.parentTaskId ?? null,
      favorite_order: task.favoriteOrder ?? null,
      completed_count: task.completedCount ?? null,
      postponed_count: task.postponedCount ?? null,
    })
    // Strip nullable columns that may not exist in Supabase yet
    if (payload.day_order == null) delete payload.day_order
    if (payload.section_id == null) delete payload.section_id
    if (payload.due_date == null) delete payload.due_date
    if (payload.due_time == null) delete payload.due_time
    if (payload.due_period == null) delete payload.due_period
    if (payload.paused_at == null) delete payload.paused_at
    if (payload.reward_id == null) delete payload.reward_id
    if (payload.recurrence == null) delete payload.recurrence
    if (payload.parent_task_id == null) delete payload.parent_task_id
    if (payload.parent_local_id == null) delete payload.parent_local_id
    if (payload.favorite_order == null) delete payload.favorite_order
    if (payload.completed_count == null) delete payload.completed_count
    if (payload.postponed_count == null) delete payload.postponed_count
    // Eine umgewandelte Unteraufgabe übernimmt die Kennung ihrer alten Zeile (T14 #179).
    if (task.adoptCloudId) payload.id = task.adoptCloudId
    return payload
  },

  buildUpdatePayload: async (task) => {
    const cloudProjectId = await resolveCloudProjectId(task.projectId)
    const cloudSectionId = await resolveCloudSectionId(task.sectionId)
    const tagIdMap = await buildLocalToCloudTagIdMap()
    const cloudTags = task.tags.flatMap(localId => {
      const cloud = tagIdMap[localId]
      return cloud ? [cloud] : []
    })
    const payload: Record<string, any> = CloudTaskPayloadSchema.parse({
      title: task.title,
      description: task.description,
      completed: task.status === 'completed',
      life_space: task.lifeSpace,
      project_id: cloudProjectId,
      section_id: cloudSectionId,
      order: task.order,
      day_order: task.dayOrder ?? null,
      tags: cloudTags,
      attention: task.attention,
      estimated_duration: task.estimatedDuration,
      duration_mode: task.durationMode,
      start_date: startDateToISO(task.startDate, task.startTime),
      start_time: task.startTime ?? null,
      start_period: task.startPeriod ?? null,
      due_date: dateToISO(task.dueDate, task.dueTime),
      due_time: task.dueTime ?? null,
      due_period: task.duePeriod ?? null,
      paused_at: task.pausedAt ? task.pausedAt.toISOString() : null,
      reward_id: task.rewardId ?? null,
      recurrence: task.recurrence ?? null,
      local_id: task.id,
      imported_from: task.importedFrom ?? null,
      parent_task_id: await resolveCloudTaskId(task.parentTaskId),
      parent_local_id: task.parentTaskId ?? null,
      favorite_order: task.favoriteOrder ?? null,
      completed_count: task.completedCount ?? null,
      postponed_count: task.postponedCount ?? null,
    })
    // section_id is always sent (including null) — like paused_at below — so that
    // unlinking a task (deleteSection sets sectionId → null) actually clears the
    // reference in the cloud. Stripping it left tasks pointing at deleted sections.
    if (!task.deletedAt) payload.deleted_at = null
    // parent_task_id / parent_local_id gehen immer mit, auch als null: Herauslösen (#204)
    // macht aus einem Kind wieder eine Aufgabe, und das muss in der Cloud ankommen.
    // paused_at is always sent (including null) so un-pausing reaches Supabase.
    // recurrence likewise — „Wiederholung entfernen" must clear the cloud column (ADR 0021).
    // favorite_order auch: „Aus Favoriten entfernen“ muss in der Cloud ankommen (#210, Checkliste 17).
    // Die Zähler (#198) dagegen nie als null: Einen Zähler leert man nicht, und eine Zeile, die den
    // Stand noch nicht kennt, darf ihn in der Cloud nicht überschreiben.
    if (payload.completed_count == null) delete payload.completed_count
    if (payload.postponed_count == null) delete payload.postponed_count
    return payload
  },

  createCloud: (userId, payload) => createCloudTask(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudTask(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudTask(cloudId, userId),
}
